import type { Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";

import { withMetrics } from "@expert/cloudwatch-metrics";
import ky from "ky";

import { env } from "@/config";
import { userCache } from "@/shared-utils";

interface SimplrSummaryRequest {
    callSid: string;
    sessionId: string;
    partner: Partner;
}

/** Sentinel sent as `x-partner-name` when the caller's partner is missing/empty, so the header never
 *  carries an empty string or the literal `"undefined"` (from a stringified `undefined`). */
const UNKNOWN_PARTNER = "unknown";

interface SimplrSummaryResponse {
    response: string;
}

/**
 * Fetches an expert session summary from the Simplr (gosimplr / LogicBuilder) HTTP invoke API.
 *
 * The base URL comes from `env.simplrApiUrl` (`VITE_SIMPLR_API_URL`); callers must not invoke this
 * when it is unset.
 */
const fetchSimplrSummary = async (logger: Logger, request: SimplrSummaryRequest, model: string) => {
    return await withMetrics(
        "getSimplrSummary",
        () =>
            ky
                .post(`${env.simplrApiUrl}/orchestrator/v1/invoke`, {
                    headers: {
                        "x-app-name": model,
                        "x-partner-name": request.partner || UNKNOWN_PARTNER,
                        "x-correlation-id": request.sessionId,
                        Authorization: `Bearer ${userCache.ssoAccessToken}`,
                        "Content-Type": "application/json",
                    },
                    json: {
                        sessionId: request.callSid,
                        sessionGroupId: request.sessionId,
                        message: crypto.randomUUID(),
                        userId: userCache.employeeId,
                        messageId: request.sessionId,
                        userType: "expert",
                    },
                    timeout: 30000,
                })
                .json<SimplrSummaryResponse>(),
        { logger: logger.child(request), rethrow: false, logResult: false, failureLogLevel: "warn" },
    );
};

export const getSimplrSummary = async (
    logger: Logger,
    request: SimplrSummaryRequest,
    model: string,
): Promise<string> => {
    if (!env.simplrApiUrl) {
        return "";
    }
    logger.debug(request, "SessionSummary | Fetching simplr generated summary");
    const simplrSummary = await fetchSimplrSummary(logger, request, model);
    return simplrSummary?.response ?? "";
};
